import { Pipe, PipeTransform } from '@angular/core';
import { DeviceTypes, PhoneTypes } from 'generated/graphql-types';
import { ExtensionTooltipModel } from 'src/app/pages/extension-register/models/extension-register-results.model';
import {
   GetExtensions_extensionsConnection_items,
   GetExtensions_extensionsConnection_items_phonesConnection_items,
} from 'src/app/pages/extension-register/queries/generated/GetExtensions';

@Pipe({
    name: 'extensionToolTipDisplay',
    standalone: false
})
export class ExtensionToolTipDisplayPipe implements PipeTransform {
   constructor() {}
   transform(extension: ExtensionTooltipModel): string {
      return this.formatExtensionTooltipHTML(extension);
   }

   formatExtensionTooltip(extension: ExtensionTooltipModel): string {
      const lines: string[] = [];

      lines.push('Extension Details');
      lines.push('');
      lines.push(`Number:\t ${extension.number}`);
      lines.push(`Phone Type: ${extension.phoneType ?? ''}`);
      lines.push(`Display Name: ${extension.displayName ?? ''}`);

      if (extension.macAddresses?.length) {
         lines.push(`Mac Address: ${extension.macAddresses.join()}`);
      }

      if (extension.models?.length) {
         lines.push(`Model: ${extension.models.join()}`);
      }

      lines.push(`Number of Lines: ${extension.numberOfLines ?? ''}`);
      lines.push(`Caller ID DID: ${extension.callerId ?? ''}`);
      lines.push(`Ring Timeout: ${extension.ringTimeout}`);

      return lines.join('\n');
   }

   formatExtensionTooltipHTML(extension: ExtensionTooltipModel): string {
      const field = (label: string, value: string | number | null | undefined) =>
         `<span class="field">${label}</span><span class="value">${value ?? ''}</span>`;

      const lines = [
         field('Number:', extension.number),
         field('Phone Type:', extension.phoneType),
         field('Display Name:', extension.displayName),
         extension.macAddresses?.length ? field('Mac Address:', extension.macAddresses.join(', ')) : '',
         extension.models?.length ? field('Model:', extension.models.join(', ')) : '',
         field('Number of Lines:', extension.numberOfLines),
         field('Caller ID DID:', extension.callerId),
         field('Ring Timeout:', extension.ringTimeout),
      ];

      return `
        <div class="tooltip-table">
          ${lines.filter(Boolean).join('')}
        </div>
      `;
   }
}

export function getExtensionTooltip(extension: GetExtensions_extensionsConnection_items): ExtensionTooltipModel {
   return {
      displayName: extension.name,
      number: extension.number,
      numberOfLines: extension.phonesConnection.items.filter((phone) => phone.phoneType === PhoneTypes.Hardphone)[0]
         ?.numberOfLineKeys,
      phoneType: determinePhoneType(extension.phonesConnection.items),
      ringTimeout: extension.extensionPhone?.ringTimeout,
      callerId: extension.extensionPhone?.defaultPhoneNumber?.phoneNumber,
      macAddresses: ([] as string[]).concat(
         ...extension.phonesConnection.items
            .filter((phone) => phone.phoneType === PhoneTypes.Hardphone)
            .map((phone) =>
               phone.devicesConnection.items
                  .map((device) => device.macAddress)
                  .filter((mac): mac is string => mac != null)
            )
      ),
      models: ([] as DeviceTypes[]).concat(
         ...extension.phonesConnection.items
            .filter((phone) => phone.phoneType === PhoneTypes.Hardphone)
            .map((phone) =>
               phone.devicesConnection.items
                  .map((device) => device.deviceTypeId)
                  .filter((id): id is DeviceTypes => id != null)
            )
      ),
   };
}

export type TooltipPhoneType = 'Hardphone' | 'Softphone' | 'Both';

export function determinePhoneType(
   phones: GetExtensions_extensionsConnection_items_phonesConnection_items[]
): TooltipPhoneType | null {
   let hasHardphone = false;
   let hasSoftphone = false;

   for (const phone of phones) {
      if (phone.phoneType === PhoneTypes.Hardphone || phone.phoneType === PhoneTypes.Handset) {
         hasHardphone = true;
      } else if (phone.phoneType === PhoneTypes.Softphone) {
         hasSoftphone = true;
      }

      if (hasHardphone && hasSoftphone) {
         return 'Both';
      }
   }

   if (hasHardphone) return 'Hardphone';
   if (hasSoftphone) return 'Softphone';

   return null;
}
